import { useRouter } from 'next/router';
import { useContext } from 'react';
import styled, { css } from 'styled-components';

import ArrowIcon from '@/components/ArrowIcon/ArrowIcon';
import { LANGUAGES } from '@/lib/constants';
import { GlobalContext } from '@/pages/_app';

const LanguageDropdown = () => {
	const { lang } = useContext(GlobalContext);
	const router = useRouter();

	const link = router?.asPath?.split('/')?.slice(2)?.join('/');
	const currentLangShortName = LANGUAGES.find(
		(item) => item.code === lang,
	)?.shortName;

	return (
		<Wrapper>
			<SelectedItem>
				<div>{currentLangShortName}</div>

				<ArrowIcon />
			</SelectedItem>

			<DropdownWrapper>
				<Triangle
					alt="dropdown-triangle"
					src="/images/icons/dropdown-triangle.png"
				/>

				<Shades />

				<DropdownList>
					{LANGUAGES.map(({ code, name }) => (
						<Language
							key={`language_dropdown_${code}`}
							$langId={code}
							$activeLang={lang}
						>
							<a href={`/${code}/${link}`}>
								<span>{name}</span>
							</a>
						</Language>
					))}
				</DropdownList>
			</DropdownWrapper>
		</Wrapper>
	);
};

const SelectedItem = styled.div`
	position: relative;
	padding-left: 20px;
	padding-right: 34px;
	height: 46px;
	background-color: transparent;
	border: 2px solid var(--color-purple-alt);
	color: var(--color-purple-alt);
	font-family: var(--font-sans);
	box-sizing: content-box;
	border-radius: 14px;
	line-height: 46px;
	text-align: center;
	text-transform: capitalize;
	font-weight: var(--font-medium);
	cursor: pointer;
	transition: 0.3s;

	@media (max-width: 1060px) {
		font-size: 14px;
		line-height: 42px;
		height: 42px;
		padding-left: 18px;
		padding-right: 32px;
	}
`;

const DropdownWrapper = styled.div`
	position: absolute;
	right: -85px;
	top: 45px;
	opacity: 0;
	transition: 0.3s;
	visibility: hidden;
`;

const Wrapper = styled.div`
	position: relative;

	&:hover {
		${SelectedItem} {
			background-color: var(--color-purple);
			border: 2px solid var(--color-purple-alt);
			color: var(--color-white);
		}

		${DropdownWrapper} {
			visibility: visible;
			opacity: 1;
		}

		svg {
			transform: translateY(-50%) rotate(0);

			path {
				stroke: var(--color-white);
			}
		}
	}
`;

const Triangle = styled.img`
	position: absolute;
	top: 10px;
	left: 50%;
	transform: translateX(-50%);
	width: 20px;
	z-index: 9999;
`;

const Shades = styled.div`
	&:after {
		content: '';
		position: absolute;
		width: 100%;
		height: 80%;
		bottom: 0px;
		left: 50%;

		transform: translateX(-50%);
		background-color: var(--color-blue);
		opacity: 0.1;
		filter: blur(22px);
		border-radius: 20px;
		z-index: -1;
	}

	&:before {
		content: '';
		position: absolute;
		width: 100%;
		height: 80%;
		bottom: -30px;
		left: 50%;

		transform: translateX(-50%);
		background-color: var(--color-blue);
		opacity: 0.1;
		filter: blur(32px);
		border-radius: 20px;
		z-index: -1;
	}
`;

const DropdownList = styled.ul`
	background-color: var(--color-white);
	padding: 8px 8px;
	min-width: 140px;
	z-index: 2000;
	margin-top: 23px;
	display: flex;

	--smooth-corners: 16.34, 6.6;
	mask-image: paint(smooth-corners);
`;

const Language = styled.li<{ $langId: string; $activeLang: string }>`
	${({ $langId, $activeLang }) =>
		$langId === $activeLang &&
		css`
			a {
				color: var(--color-black) !important;
			}
		`}

	mask-image: paint(smooth-corners);
	--smooth-corners: 11.34, 9.6;

	img {
		width: 30px;
	}

	a {
		display: block;
		width: 80px;
		padding: 10px 0;
		padding-left: 10px;
		padding-right: 10px;
		text-decoration: none;
		font-family: var(--font-sans);

		font-weight: var(--font-medium);
		font-size: 14px;
		line-height: 20px;
		color: var(--color-purple);
		text-align: center;
		border-radius: 12px;
		transition: 0.3s;
	}

	&:hover {
		a {
			color: var(--color-white) !important;
			background-color: var(--color-purple);
			border-radius: 12px;
		}
	}
`;

export default LanguageDropdown;
